import { useCallback, useEffect, useState } from 'react';
import { Keyboard, LogOut, Settings, Users } from 'lucide-react';
import { AccountSwitcher, AppLauncher, ConfirmDialog, announceAccountsChanged, tabAccount, useAccountsChanged, useToast, type DeviceAccount } from '@dugzi/ui';
import { APP, api } from '../lib/api';
import type { Me } from '../lib/types';

let known: DeviceAccount[] = [];

/** The shared account switcher (D-028): every account signed in to Drive in this browser. */
export function AccountMenu({ me, onSettings, onShortcuts }: { me: Me; onSettings(): void; onShortcuts(): void }) {
  const toast = useToast();
  const [accounts, setAccounts] = useState<DeviceAccount[]>(known);
  const [confirmOut, setConfirmOut] = useState<null | 'one' | 'all'>(null);
  const load = useCallback(() => api<{ accounts: DeviceAccount[] }>('/api/accounts').then((r) => { known = r.accounts; setAccounts(r.accounts); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useAccountsChanged(APP, load);
  const current: DeviceAccount = { id: me.user.id, name: me.user.name, address: me.user.email, accent: me.user.accent, space: me.user.space };
  const list = accounts.some((a) => a.id === current.id) ? accounts : [current, ...accounts];

  const switchTo = (a: DeviceAccount) => {
    tabAccount.set(APP, a.id);
    toast.show(`Now using ${a.address}`);
    // Files belong to accounts: go back to My Drive of the account you switched to.
    window.location.assign('/');
  };

  async function signOut(all: boolean) {
    const targets = all ? list : [current];
    let redirect = '/';
    let remaining = 0;
    for (const a of targets) {
      const r = await api<{ redirect: string; remaining: number }>('/api/auth/logout', { body: { account: a.id } }).catch(() => null);
      if (r) { redirect = r.redirect; remaining = r.remaining; }
    }
    announceAccountsChanged(APP);
    if (all) redirect = redirect.replace(/account=[^&]+/, 'all=1');
    if (!all && remaining > 0) tabAccount.set(APP, null);
    window.location.assign(redirect);
  }

  return (
    <>
      <AppLauncher current="drive" accountsUrl={me.accountsUrl} account={me.user.id} />
      <AccountSwitcher manageUrl={`${me.accountsUrl}/?account=${encodeURIComponent(me.user.id)}`}
        current={current}
        accounts={list}
        shortcuts
        onSwitch={switchTo}
        onOpenInNewTab={(a) => window.open(`/?account=${encodeURIComponent(a.id)}`, '_blank', 'noopener')}
        onAdd={() => window.location.assign(`/api/auth/login?add=1&return_to=${encodeURIComponent('/')}`)}
        onSignOut={() => setConfirmOut('one')}
        onSignOutAll={() => setConfirmOut('all')}
      >
        <button role="menuitem" className="dz-menu__item" onClick={onShortcuts}><Keyboard aria-hidden />Keyboard shortcuts</button>
        <button role="menuitem" className="dz-menu__item" onClick={onSettings} data-testid="drive-settings"><Settings aria-hidden />Drive settings and your data</button>
      </AccountSwitcher>
      <ConfirmDialog open={confirmOut === 'one'} onClose={() => setConfirmOut(null)} onConfirm={() => signOut(false)} title={list.length > 1 ? `Sign out of ${current.address}?` : 'Sign out of Dugzi?'} confirmLabel="Sign out" icon={<LogOut aria-hidden />}>
        <p className="dz-muted">{list.length > 1
          ? 'This account will be signed out of Drive, Account and every other Dugzi app on this device. Your other accounts stay signed in.'
          : "You'll be signed out of Dugzi on this device: Drive, Account and every other Dugzi app. Signing back in takes a tap with your passkey, or a code."}</p>
      </ConfirmDialog>
      <ConfirmDialog open={confirmOut === 'all'} onClose={() => setConfirmOut(null)} onConfirm={() => signOut(true)} title="Sign out of all accounts?" confirmLabel="Sign out of all" icon={<Users aria-hidden />}>
        <p className="dz-muted">All {list.length} accounts will be signed out of every Dugzi app on this device.</p>
      </ConfirmDialog>
    </>
  );
}
